// @ts-strict-ignore
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Error } from '#components/alerts';
import { Link } from '#components/common/Link';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
} from '#components/common/Modal';
import { FormField, FormLabel } from '#components/forms';
import { useCurrentAccess } from '#hooks/useCurrentAccess';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import { getSecretsError } from '#util/error';

type PluggyAiInitialiseProps = Extract<
  ModalType,
  { name: 'pluggyai-init' }
>['options'];

export const PluggyAiInitialiseModal = ({
  onSuccess,
  credentialSource,
}: PluggyAiInitialiseProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cloudFileId, isAdmin: canSetGlobalCredentials } = useCurrentAccess();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [perBudgetFile, setPerBudgetFile] = useState(
    credentialSource === 'per-budget-file' || !canSetGlobalCredentials,
  );
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(
    t('It is required to provide both the client id and client secret.'),
  );

  const onSubmit = async (close: () => void) => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setIsValid(false);
      setError(
        t('It is required to provide both the client id and client secret.'),
      );
      return;
    }

    setIsLoading(true);

    const fileId = perBudgetFile ? cloudFileId : null;
    if (perBudgetFile && !fileId) {
      setIsLoading(false);
      setIsValid(false);
      setError(t('Budget file ID is required.'));
      return;
    }

    const result = await send('pluggyai-set-default-credentials', {
      clientId: clientId.trim(),
      clientSecret: clientSecret.trim(),
      perBudgetFile,
    });

    if (result?.error) {
      setIsLoading(false);
      setIsValid(false);
      setError(getSecretsError(result.error, result.reason));
      return;
    }

    setIsValid(true);
    dispatch(
      addNotification({
        notification: {
          type: 'message',
          message: t('Pluggy.ai default credentials saved successfully.'),
        },
      }),
    );
    onSuccess(perBudgetFile);
    setIsLoading(false);
    close();
  };

  return (
    <Modal name="pluggyai-init" containerProps={{ style: { width: '30vw' } }}>
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Set-up Pluggy.ai')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View style={{ display: 'flex', gap: 10 }}>
            <Text>
              <Trans>
                In order to enable bank sync via Pluggy.ai (only for Brazilian
                banks) you will need to create access credentials. This can be
                done by creating an account with{' '}
                <Link
                  variant="external"
                  to="https://actualbudget.org/docs/advanced/bank-sync/"
                  linkColor="purple"
                >
                  Pluggy.ai
                </Link>
                .
              </Trans>
            </Text>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <Text>
                <Trans>For this budget only</Trans>
              </Text>
              <Toggle
                id="pluggyai-per-budget-file"
                isOn={perBudgetFile}
                isDisabled={!canSetGlobalCredentials}
                onToggle={setPerBudgetFile}
              />
            </View>

            <FormField>
              <FormLabel title={t('Client ID:')} htmlFor="client-id-field" />
              <InitialFocus>
                <Input
                  id="client-id-field"
                  type="text"
                  value={clientId}
                  onChangeValue={value => {
                    setClientId(value);
                    setIsValid(true);
                  }}
                />
              </InitialFocus>
            </FormField>

            <FormField>
              <FormLabel
                title={t('Client Secret:')}
                htmlFor="client-secret-field"
              />
              <Input
                id="client-secret-field"
                type="password"
                value={clientSecret}
                onChangeValue={value => {
                  setClientSecret(value);
                  setIsValid(true);
                }}
              />
            </FormField>

            {!isValid && <Error>{error}</Error>}
          </View>

          <ModalButtons>
            <ButtonWithLoading
              variant="primary"
              isLoading={isLoading}
              onPress={() => {
                void onSubmit(() => state.close());
              }}
            >
              <Trans>Save and continue</Trans>
            </ButtonWithLoading>
          </ModalButtons>
        </>
      )}
    </Modal>
  );
};
